/**
 * MutualFundsTab — Research UI for mutual funds.
 *
 * Features:
 * - Search + add funds to watchlist
 * - Watchlist table with key metrics (latest NAV, category, fund house)
 * - "Details" button opens FundDetailModal
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    searchFunds,
    getWatchlist,
    addToWatchlist,
    removeFromWatchlist,
} from '../../services/fundsService';
import FundDetailModal from './FundDetailModal';
import '../../styles/FundsPage.css';

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const MutualFundsTab = () => {
    // ── Search state ──
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const searchTimeout = useRef(null);

    // ── Watchlist state ──
    const [watchlist, setWatchlist] = useState([]);
    const [wlLoading, setWlLoading] = useState(true);
    const [adding, setAdding] = useState(null); // schemeCode being added

    // ── Detail modal ──
    const [detailFund, setDetailFund] = useState(null);

    // ── Fetch watchlist ──
    const fetchWatchlist = useCallback(async () => {
        setWlLoading(true);
        try {
            const raw = await getWatchlist();
            let data = unwrap(raw);
            setWatchlist(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Watchlist fetch failed:', err);
            // Graceful fallback – use localStorage
            try {
                const local = JSON.parse(localStorage.getItem('fund_watchlist') || '[]');
                setWatchlist(local);
            } catch {
                setWatchlist([]);
            }
        } finally {
            setWlLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWatchlist();
    }, [fetchWatchlist]);

    // ── Search (debounced) ──
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        searchTimeout.current = setTimeout(async () => {
            setSearching(true);
            try {
                const raw = await searchFunds(query);
                const data = unwrap(raw);
                setSearchResults(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Fund search failed:', err);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 400);
        return () => clearTimeout(searchTimeout.current);
    }, [query]);

    // ── Add to watchlist ──
    const handleAdd = async (scheme) => {
        const code = scheme.schemeCode;
        setAdding(code);
        try {
            await addToWatchlist(code);
            // Also persist locally for fallback
            const local = JSON.parse(localStorage.getItem('fund_watchlist') || '[]');
            if (!local.find((w) => w.schemeCode === code)) {
                local.push({ schemeCode: code, schemeName: scheme.schemeName });
                localStorage.setItem('fund_watchlist', JSON.stringify(local));
            }
            // Re-fetch
            await fetchWatchlist();
            setQuery('');
            setSearchResults([]);
        } catch (err) {
            console.error('Failed to add to watchlist:', err);
            alert('Failed to add fund. Please try again.');
        } finally {
            setAdding(null);
        }
    };

    // ── Remove from watchlist ──
    const handleRemove = async (schemeCode) => {
        try {
            await removeFromWatchlist(schemeCode);
            // Update local
            const local = JSON.parse(localStorage.getItem('fund_watchlist') || '[]');
            localStorage.setItem(
                'fund_watchlist',
                JSON.stringify(local.filter((w) => w.schemeCode !== schemeCode))
            );
            setWatchlist((prev) => prev.filter((w) => w.schemeCode !== schemeCode));
        } catch (err) {
            console.error('Failed to remove from watchlist:', err);
        }
    };

    const inWatchlist = (code) => watchlist.some((w) => w.schemeCode === code);

    return (
        <div className="mf-section">
            {/* Search */}
            <div className="mf-search-row">
                <input
                    className="mf-search-input"
                    type="text"
                    placeholder="Search mutual funds by name (e.g. HDFC Top 100, Parag Parikh Flexi Cap)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {searching && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>Searching…</span>
                )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
                <div className="mf-search-results">
                    {searchResults.map((s) => (
                        <div key={s.schemeCode} className="mf-search-result-item">
                            <div>
                                <div className="mf-result-name">{s.schemeName}</div>
                                <div className="mf-result-code">Code: {s.schemeCode}</div>
                            </div>
                            {inWatchlist(s.schemeCode) ? (
                                <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>
                                    ✓ In watchlist
                                </span>
                            ) : (
                                <button
                                    className="mf-result-add-btn"
                                    onClick={() => handleAdd(s)}
                                    disabled={adding === s.schemeCode}
                                >
                                    {adding === s.schemeCode ? 'Adding…' : '+ Add'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Watchlist */}
            <div className="mf-watchlist-section">
                <h3 className="mf-watchlist-title">
                    My Watchlist ({watchlist.length})
                </h3>

                {wlLoading ? (
                    <div className="funds-loading">Loading watchlist…</div>
                ) : watchlist.length === 0 ? (
                    <div className="funds-empty">
                        <p>Your watchlist is empty. Search and add mutual funds above.</p>
                    </div>
                ) : (
                    <div className="mf-watchlist-table-wrap">
                        <table className="mf-watchlist-table">
                            <thead>
                                <tr>
                                    <th>Fund Name</th>
                                    <th>Fund House</th>
                                    <th>Category</th>
                                    <th>Latest NAV</th>
                                    <th>NAV Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {watchlist.map((fund) => (
                                    <tr key={fund.schemeCode}>
                                        <td style={{ whiteSpace: 'normal', minWidth: 200, maxWidth: 340 }}>
                                            {fund.schemeName || `Scheme ${fund.schemeCode}`}
                                        </td>
                                        <td>{fund.fundHouse || <span className="mf-metric-na">—</span>}</td>
                                        <td>{fund.schemeCategory || <span className="mf-metric-na">—</span>}</td>
                                        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                                            {fund.latestNav != null ? fund.latestNav.toFixed(2) : <span className="mf-metric-na">—</span>}
                                        </td>
                                        <td>{fund.navDate || <span className="mf-metric-na">—</span>}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                <button
                                                    className="mf-details-btn"
                                                    onClick={() => setDetailFund(fund)}
                                                >
                                                    Details
                                                </button>
                                                <button
                                                    className="mf-remove-btn"
                                                    onClick={() => handleRemove(fund.schemeCode)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Detail modal */}
            {detailFund && (
                <FundDetailModal
                    fund={detailFund}
                    onClose={() => setDetailFund(null)}
                />
            )}
        </div>
    );
};

export default MutualFundsTab;
